import { validateTenDigitPhone } from './utils';

export type OrgUserOption = {
    label: string;
    value: string;
    /** Muted detail rendered on the right of the option row, opposite the label. */
    secondaryLabel?: string;
    /** Listed but refused at selection, because this row is not usable as a value. */
    disabled?: boolean;
    /** The response row this option was shaped from, attached where the endpoint offers a fix for it. */
    sourceRow?: any;
};

/** A detail the row has no value for: shown as a dash, rather than naming what is missing. */
const MISSING_DETAIL = '-';

/**
 * What a user row shows on each side, and whether it can be picked at all.
 *
 * A chosen user is pushed to an external lending platform, which requires both an email address
 * and a 10-digit mobile number — and that push happens in the background, so a user missing
 * either fails there invisibly. Such a row is therefore listed and refused at selection rather
 * than hidden: someone who cannot find a colleague they expected reads the whole list as broken.
 * It is left unexplained and merely unavailable, carrying the response row it was read from so
 * the missing field can be filled without leaving the drawer. The 10-digit check is the app's one
 * phone rule, shared with the invite and add-director flows, and an Indian-number rule — which is
 * why a number that passes it is shown under +91.
 */
const describeOrgUser = (orgUser: any): Omit<OrgUserOption, 'value'> => {
    const email = typeof orgUser?.emailId === 'string' ? orgUser.emailId.trim() : '';
    const mobile = typeof orgUser?.mobileNumber === 'string' ? orgUser.mobileNumber.trim() : '';
    const hasMobile = validateTenDigitPhone(mobile);
    const row = {
        label: hasMobile ? `${orgUser.name} (+91 ${mobile})` : orgUser.name,
        secondaryLabel: email || MISSING_DETAIL,
    };

    return email && hasMobile ? row : { ...row, disabled: true, sourceRow: orgUser };
};

// A single organisation's user list: { data: [{ name, userId, emailId, mobileNumber, ... }] }.
export const mapOrgUsersResponse = (responseBody: any): OrgUserOption[] =>
    (Array.isArray(responseBody?.data) ? responseBody.data : [])
        .filter((orgUser: any) => !!orgUser?.userId && typeof orgUser?.name === 'string' && orgUser.name !== '')
        .map((orgUser: any) => ({ value: orgUser.userId, ...describeOrgUser(orgUser) }));
